import { createFileRoute } from "@tanstack/react-router";
import {
	Banknote,
	Check,
	Compass,
	Download,
	Megaphone,
	Scale,
	Star,
	UserX,
	Wallet,
} from "lucide-react";
import { BookingFab } from "#/components/booking-fab";
import { LeadForm } from "#/components/lead-form";
import { LogoMark } from "#/components/logo";
import { Accordion, ForwardArrow } from "#/components/ui";
import { VariantChrome } from "#/components/variant-chrome";
import { useI18n } from "#/lib/i18n";
import { getDefaultTraining } from "#/lib/training";

export const Route = createFileRoute("/hero-b")({
	head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
	loader: () => getDefaultTraining(),
	component: HeroB,
});

const PILLAR_ICONS = [Banknote, Scale, Megaphone];
const PAIN_ICONS = [Compass, Wallet, Scale, UserX];

/** Cinematic numbered label: gold digits + hairline rule on dark. */
function DarkLabel({
	index,
	children,
}: {
	index: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-center gap-3">
			<span className="index-num font-display text-sm font-bold text-gold">
				{index}
			</span>
			<span aria-hidden className="h-px w-8 bg-white/25" />
			<span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
				{children}
			</span>
		</div>
	);
}

function HeroB() {
	const { t } = useI18n();
	const training = Route.useLoaderData();
	const format = t.hero.eyebrow.split("·").map((s) => s.trim());

	return (
		<div className="min-h-screen overflow-x-clip bg-navy-900 text-white">
			<VariantChrome active="b" dark />

			{/* ---------- hero ---------- */}
			<section className="grain relative overflow-hidden">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_55%_at_80%_-5%,rgb(23_166_177/0.3),transparent_55%),radial-gradient(45%_45%_at_0%_100%,rgb(224_166_60/0.18),transparent_55%)]"
				/>

				<div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 pt-28 sm:px-8 lg:min-h-screen lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:pt-24">
					{/* copy */}
					<div className="animate-fade-up">
						<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.7rem] font-bold uppercase tracking-[0.24em] text-teal-100">
							{format.map((f, i) => (
								<span key={f} className="flex items-center gap-3">
									{i > 0 && (
										<span
											aria-hidden
											className="h-1 w-1 rounded-full bg-gold"
										/>
									)}
									{f}
								</span>
							))}
						</div>

						<h1 className="mt-6 font-display font-bold leading-[1.03] text-white text-[clamp(2.7rem,7.5vw,5.2rem)]">
							{t.hero.titleLead}{" "}
							<span className="text-gold">{t.hero.titleHighlight}</span>
						</h1>

						<p className="mt-6 max-w-lg text-lg leading-relaxed text-white/65">
							{t.hero.subtitle}
						</p>

						<div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
							<a
								href="#register"
								className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-base font-bold text-navy-900 shadow-gold transition-all hover:-translate-y-0.5 hover:bg-gold-600 sm:w-auto"
							>
								{t.hero.ctaPrimary}
								<ForwardArrow className="h-4 w-4 transition-transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
							</a>
							<a
								href="#course"
								className="inline-flex w-full items-center justify-center rounded-full border border-white/25 px-7 py-4 text-base font-bold text-white transition-colors hover:bg-white/10 sm:w-auto"
							>
								{t.hero.ctaSecondary}
							</a>
						</div>

						<div className="mt-8 flex items-center gap-3">
							<div className="flex text-gold">
								{[0, 1, 2, 3, 4].map((i) => (
									<Star key={i} className="h-4 w-4 fill-current" />
								))}
							</div>
							<span className="text-sm text-white/60">{t.hero.trust}</span>
						</div>
					</div>

					{/* portrait — teal duotone, thin gold rule */}
					<div className="relative mx-auto w-full max-w-md animate-fade-up [animation-delay:120ms] lg:max-w-none">
						<div className="relative overflow-hidden rounded-[2rem] border border-gold/40 bg-navy">
							<img
								src="/team/ayoub-portrait.webp"
								alt={t.hero.portraitAlt}
								width={700}
								height={840}
								className="aspect-[5/6] w-full object-cover object-top opacity-90 [filter:grayscale(0.15)]"
							/>
							<div
								aria-hidden
								className="absolute inset-0 mix-blend-color bg-teal/40"
							/>
							<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent p-6 pt-16">
								<div className="font-display text-xl font-bold text-white">
									{t.trainer.name}
								</div>
								<div className="text-sm text-gold">{t.trainer.role}</div>
							</div>
						</div>
					</div>
				</div>

				{/* bottom metric bar */}
				<div className="relative border-t border-white/10">
					<div className="mx-auto grid max-w-6xl grid-cols-2 gap-y-6 px-5 py-8 sm:px-8 lg:grid-cols-5">
						{t.stats.map((s, i) => (
							<div
								key={s.label}
								className={`px-2 text-center max-lg:last:col-span-2 sm:px-6 ${
									i > 0 ? "lg:border-s lg:border-white/10" : ""
								}`}
							>
								<div className="font-display text-3xl font-bold text-white lg:text-4xl">
									{s.value}
								</div>
								<div className="mt-1 text-xs text-white/55">{s.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ---------- pains ---------- */}
			<section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20 lg:py-24">
				<div className="mx-auto max-w-2xl text-center">
					<div className="flex justify-center">
						<DarkLabel index="01">{t.pains.eyebrow}</DarkLabel>
					</div>
					<h2 className="mt-5 font-display font-bold leading-tight text-white text-[clamp(1.9rem,4vw,2.75rem)]">
						{t.pains.heading}
					</h2>
					<p className="mt-4 text-base text-white/60">{t.pains.sub}</p>
				</div>

				<div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{t.pains.items.map((pain, i) => {
						const Icon = PAIN_ICONS[i];
						return (
							<article
								key={pain.title}
								className="group relative h-full overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:ring-gold/40"
							>
								<span
									aria-hidden
									className="index-num absolute end-4 top-3 font-display text-4xl font-bold text-white/10 transition-colors group-hover:text-gold/40"
								>
									{`0${i + 1}`}
								</span>
								<span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold">
									<Icon className="h-5 w-5" />
								</span>
								<h3 className="mt-5 font-display text-lg font-bold text-white">
									{pain.title}
								</h3>
								<p className="mt-1.5 text-sm leading-relaxed text-white/55">
									{pain.text}
								</p>
							</article>
						);
					})}
				</div>

				<div className="mt-8 flex items-center justify-center gap-3 rounded-2xl border border-gold/30 bg-gold/10 px-6 py-5 text-center">
					<span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gold text-navy-900">
						<Check className="h-4 w-4" strokeWidth={3} />
					</span>
					<p className="font-display text-base font-bold text-white sm:text-lg">
						{t.pains.solution}
					</p>
				</div>
			</section>

			{/* ---------- pillars (solution) ---------- */}
			<section id="course" className="scroll-mt-20 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
						<div className="flex flex-col items-start gap-4">
							<DarkLabel index="02">{t.pillars.eyebrow}</DarkLabel>
							<h2 className="max-w-lg font-display font-bold leading-tight text-white text-[clamp(1.9rem,4vw,2.75rem)]">
								{t.pillars.heading}
							</h2>
						</div>
						<p className="max-w-xs text-sm leading-relaxed text-white/60">
							{t.pillars.sub}
						</p>
					</div>

					<div className="mt-12 grid gap-5 md:grid-cols-3">
						{t.pillars.items.map((pillar, i) => {
							const Icon = PILLAR_ICONS[i];
							return (
								<article
									key={pillar.title}
									className="group relative h-full overflow-hidden rounded-[1.75rem] bg-navy p-7 ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-glow hover:ring-teal/40"
								>
									<span
										aria-hidden
										className="absolute inset-x-7 top-0 h-1 origin-[right] scale-x-0 rounded-b bg-gold transition-transform duration-300 group-hover:scale-x-100 ltr:origin-[left]"
									/>
									<div className="flex items-center justify-between">
										<span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal text-white transition-transform group-hover:scale-110">
											<Icon className="h-5 w-5" />
										</span>
										<span className="index-num font-display text-3xl font-bold text-white/15 transition-colors group-hover:text-gold">
											{`0${i + 1}`}
										</span>
									</div>
									<h3 className="mt-14 font-display text-xl font-bold text-white">
										{pillar.title}
									</h3>
									<p className="mt-2 text-sm leading-relaxed text-white/60">
										{pillar.text}
									</p>
								</article>
							);
						})}
					</div>

					{/* technical-card downloads */}
					<div className="mt-10 flex flex-col items-center justify-center gap-3 rounded-[1.75rem] bg-white/5 p-6 ring-1 ring-white/10 sm:flex-row">
						<span className="text-sm font-bold text-white">
							{t.pillars.downloadLabel}
						</span>
						<div className="flex flex-wrap items-center justify-center gap-3">
							<a
								href="/downloads/fiche-technique-ar.pdf"
								download
								className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-900 shadow-gold transition-all hover:-translate-y-0.5 hover:bg-gold-600"
							>
								<Download className="h-4 w-4" />
								عربية (AR)
							</a>
							<a
								href="/downloads/fiche-technique-fr.pdf"
								download
								className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
							>
								<Download className="h-4 w-4" />
								Français (FR)
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- trainer ---------- */}
			<section id="trainer" className="scroll-mt-20 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
					<div className="relative order-2 lg:order-1">
						<div className="relative overflow-hidden rounded-[2rem] border border-teal/40 bg-navy shadow-lift">
							<img
								src="/team/ayoub-avatar.webp"
								alt={t.trainer.avatarAlt}
								width={460}
								height={460}
								className="aspect-square w-full object-cover opacity-90 [filter:grayscale(0.15)]"
							/>
							<div
								aria-hidden
								className="absolute inset-0 mix-blend-color bg-teal/40"
							/>
							<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-900 via-navy-900/50 to-transparent p-6 pt-16">
								<div className="font-display text-xl font-bold text-white">
									{t.trainer.name}
								</div>
								<div className="text-sm text-gold">{t.trainer.role}</div>
							</div>
						</div>

						<div className="mt-8 flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
							<div className="index-num font-display text-4xl font-bold text-gold">
								+10,000
							</div>
							<div className="text-sm leading-snug text-white/60">
								{t.trainer.statLabel}
							</div>
						</div>

						<p className="mt-6 text-sm leading-relaxed text-white/55">
							{t.trainer.bio}
						</p>
					</div>

					<div className="order-1 lg:order-2">
						<div className="flex flex-col items-start gap-4">
							<DarkLabel index="03">{t.trainer.eyebrow}</DarkLabel>
							<h2 className="font-display font-bold leading-tight text-white text-[clamp(1.9rem,4vw,2.75rem)]">
								{t.trainer.heading}
							</h2>
							<p className="text-base leading-relaxed text-white/60">
								{t.trainer.sub}
							</p>
						</div>

						<div className="mt-8">
							<Accordion items={t.trainer.why} tone="dark" />
						</div>
					</div>
				</div>
			</section>

			{/* ---------- outcomes ---------- */}
			<section id="learn" className="scroll-mt-20 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="mb-8 overflow-hidden rounded-[2rem] bg-teal/10 p-8 ring-1 ring-teal/25 sm:p-10">
						<div className="mb-5 flex items-center gap-3">
							<span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-teal text-white">
								<Compass className="h-5 w-5" />
							</span>
							<h2 className="font-display text-2xl font-bold leading-tight text-white sm:text-3xl">
								{t.outcomes.audienceHeading}
							</h2>
						</div>
						<div className="flex flex-wrap gap-2.5">
							{t.outcomes.audience.map((item) => (
								<span
									key={item}
									className="rounded-full bg-white/5 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/15"
								>
									{item}
								</span>
							))}
						</div>
					</div>

					<div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
						<div className="rounded-[2rem] bg-navy p-8 ring-1 ring-white/10 sm:p-10">
							<div className="mb-6">
								<DarkLabel index="04">{t.outcomes.objectivesHeading}</DarkLabel>
							</div>
							<ul className="flex flex-col gap-4">
								{t.outcomes.objectives.map((item, i) => (
									<li key={item} className="flex items-start gap-4">
										<span className="index-num mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold/15 font-display text-xs font-bold text-gold">
											{i + 1}
										</span>
										<span className="text-sm leading-relaxed text-white/80">
											{item}
										</span>
									</li>
								))}
							</ul>
						</div>

						<div className="grain relative flex flex-col justify-center overflow-hidden rounded-[2rem] border border-gold/30 bg-gold/10 p-8">
							<div
								aria-hidden
								className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_80%_at_50%_0%,rgb(224_166_60/0.18),transparent_60%)]"
							/>
							<div className="relative font-display text-6xl font-bold text-gold">
								{t.outcomes.practiceValue}
							</div>
							<p className="relative mt-3 text-sm leading-relaxed text-white/70">
								{t.outcomes.practiceNote}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- faq ---------- */}
			<section id="faq" className="scroll-mt-20 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
					<div className="flex flex-col items-start gap-4">
						<DarkLabel index="05">{t.faq.eyebrow}</DarkLabel>
						<h2 className="font-display font-bold leading-tight text-white text-[clamp(1.9rem,4vw,2.75rem)]">
							{t.faq.heading}
						</h2>
						<p className="text-sm leading-relaxed text-white/60">{t.faq.sub}</p>
					</div>
					<div>
						<Accordion items={t.faq.items} tone="dark" />
					</div>
				</div>
			</section>

			{/* ---------- register ---------- */}
			<section id="register" className="scroll-mt-20 px-5 pb-20 sm:px-8">
				<div className="grain relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] border border-white/10 bg-navy">
					<div
						aria-hidden
						className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_60%_at_15%_0%,rgb(23_166_177/0.22),transparent_60%),radial-gradient(40%_50%_at_100%_100%,rgb(224_166_60/0.14),transparent_60%)]"
					/>
					<div className="relative grid lg:grid-cols-[1fr_0.85fr]">
						<div className="p-8 sm:p-12 lg:p-14">
							<DarkLabel index="06">{t.register.eyebrow}</DarkLabel>
							<h2 className="mt-5 max-w-md font-display text-3xl font-bold leading-tight text-white sm:text-4xl">
								{t.register.heading}
							</h2>
							<p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">
								{t.register.sub}
							</p>

							<div className="mt-8 rounded-[1.75rem] bg-canvas p-6 sm:p-7">
								<LeadForm source="preview-b" trainingId={training.id} />
							</div>
						</div>

						<div className="relative hidden lg:block">
							<img
								src="/team/ayoub-training.webp"
								alt={t.register.imageAlt}
								width={560}
								height={686}
								className="h-full w-full object-cover opacity-90"
							/>
							<div
								aria-hidden
								className="absolute inset-0 bg-gradient-to-l from-transparent to-navy rtl:bg-gradient-to-r"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- footer ---------- */}
			<footer className="border-t border-white/10 py-12">
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="flex flex-col items-center gap-6 border-b border-white/10 pb-8 text-center sm:flex-row sm:justify-between sm:text-start">
						<LogoMark className="h-9 w-auto" />
						<p className="max-w-xs text-sm text-white/50">{t.footer.tagline}</p>
						<a
							href="#register"
							className="rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy-900 shadow-gold transition-colors hover:bg-gold-600"
						>
							{t.footer.book}
						</a>
					</div>
					<div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-white/40 sm:flex-row">
						<p>{t.footer.rights}</p>
						<p dir="ltr">moustachiracademy@moustachir.dz · www.moustachir.dz</p>
					</div>
				</div>
			</footer>

			<BookingFab href="#register" />
		</div>
	);
}
